import { Loader } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import {
  useUpdateMailboxWarmup,
  useWarmupAccountToggle,
} from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import { extractErrorMessage } from "@/lib/utils";
import { ISenderProfile, MailboxType } from "@/types/mailbox.types";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const WarmupToggleCell = ({
  data,
  type,
  onBeforeEnableWarmup,
}: {
  data: MailboxType | ISenderProfile;
  type: "sender_profile" | "mailbox";
  /** When provided (mailbox only), enabling warmup opens B2B settings first instead of toggling directly */
  onBeforeEnableWarmup?: (data: MailboxType | ISenderProfile) => void;
}) => {
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateMailboxWarmup,
    isPending: isMailboxWarmupLoading,
  } = useUpdateMailboxWarmup();

  const {
    mutateAsync: toggleWarmupAccount,
    isPending: isWarmupAccountToggling,
  } = useWarmupAccountToggle();

  const onToggle = async (checked: boolean) => {
    try {
      if (type === "mailbox") {
        if (!data?.subscriptionId || (!data?.planId && !data?.addonPlan)) {
          toast.error(
            "Please assign a subscription to the mailbox before enabling warm-up."
          );

          return;
        }

        if (checked && onBeforeEnableWarmup) {
          onBeforeEnableWarmup(data);
          return;
        }

        const response = await updateMailboxWarmup({
          id: data?._id,
          mailboxData: { isWarmupEnabled: checked },
        });

        const eventName = checked
          ? "mailbox_warmup_toggled_on"
          : "mailbox_warmup_toggled_off";

        window.cioanalytics?.track(eventName, {
          email: data?.email,
        });
      }

      if (type === "sender_profile") {
        if (!data?.subscriptionId || (!data?.planId && !data?.addonPlan)) {
          toast.error(
            "Please assign a subscription to the sender profile before toggling warm-up."
          );

          return;
        }

        await toggleWarmupAccount({ id: data?._id, enabled: checked, email: data?.email as string });

        const eventName = checked
          ? "sender_profile_warmup_toggled_on"
          : "sender_profile_warmup_toggled_off";

        window.cioanalytics?.track(eventName, {
          email: data?.email,
        });
      }

      optimisticUpdate({
        type,
        data: {
          _id: data?._id,
          email: data?.email,
          isWarmupEnabled: checked,
        },
        queryClient,
      });

      // Only show toast after successful update
      toast.success("Warmup toggled successfully");
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to toggle warmup");
      toast.error(message);
      console.error("Failed to toggle warmup:", error);
    }
  };

  const isLoading = isMailboxWarmupLoading || isWarmupAccountToggling;

  // console.log("data from warmup toggle cell", data);

  return (
    <div className="flex items-center justify-center h-full">
      {isLoading ? (
        <Loader className="size-4 animate-spin" />
      ) : (
        <Switch
          checked={data?.isWarmupEnabled}
          onCheckedChange={onToggle}
          disabled={isLoading || !hasPermission("mailboxes:edit")}
        />
      )}
    </div>
  );
};

const optimisticUpdate = ({
  type,
  data,
  queryClient,
}: {
  type: "mailbox" | "sender_profile";
  data: {
    _id: string;
    isWarmupEnabled: boolean;
    email: string;
  };
  queryClient: QueryClient;
}) => {
  if (type === "mailbox") {
    // get active page data
    const activePageQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["mailboxes"] }) // match all pagination queries
      .filter((query) => query.isActive());

    if (activePageQueries.length === 0) {
      throw new Error("No active page queries found");
    }

    const activePageQuery = activePageQueries[0];

    const oldData = activePageQuery.state.data as {
      data: MailboxType[];
      total: number;
    };
    // update the data

    const updatedData = oldData.data.map((mailbox: MailboxType) => {
      if (mailbox._id === data._id) {
        return {
          ...mailbox,
          isWarmupEnabled: data.isWarmupEnabled,
        };
      }
      return mailbox;
    });

    const newData = {
      ...oldData,
      data: updatedData,
    };

    // set the data in tanstack query cache
    queryClient.setQueryData(activePageQuery.queryKey, newData);
  }

  if (type === "sender_profile") {
    const activePageQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["warmupAccounts"] }) // match all pagination queries
      .filter((query) => query.isActive());

    if (activePageQueries.length === 0) {
      throw new Error("No active page queries found");
    }

    const activePageQuery = activePageQueries[0];

    const oldData = activePageQuery.state.data as ISenderProfile[];

    // update the data
    const updatedData = oldData.map((senderProfile: ISenderProfile) => {
      if (senderProfile._id === data._id) {
        return {
          ...senderProfile,
          isWarmupEnabled: data.isWarmupEnabled,
          email: data.email,
        };
      }
      return senderProfile;
    });

    // set the data in tanstack query cache
    queryClient.setQueryData(activePageQuery.queryKey, updatedData);
  }
};

export default WarmupToggleCell;
